import { useAuth } from "@clerk/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { Download, Loader2, RefreshCw } from "lucide-react"
import * as React from "react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { AiMatcherGrid } from "@/features/ai-matcher/AiMatcherGrid"
import { useOrpc } from "@/integrations/orpc"

export const Route = createFileRoute("/catalogue/ai-matcher/$runId")({
	component: AiMatcherRunPage,
})

function AiMatcherRunPage() {
	const { runId } = Route.useParams()
	const { client, isAuthReady } = useOrpc()
	const { isSignedIn } = useAuth()

	const queryClient = useQueryClient()
	const runQueryKey = ["aiMatcher", "getRun", runId]
	/** Bumped after a re-match so the grid drops edits made on the old slots. */
	const [gridVersion, setGridVersion] = React.useState(0)

	/**
	 * Re-run the matcher on the run's stored catalogs, then the AI pass on the
	 * slots it leaves open. Upgrades runs prepared by an older matcher.
	 */
	const rematchMutation = useMutation({
		mutationFn: async () => {
			const matched = await client.aiMatcher.rematch({ runId })
			setGridVersion((v) => v + 1)
			await queryClient.refetchQueries({ queryKey: runQueryKey })
			const ai =
				matched.unresolved > 0
					? await client.aiMatcher.runLlm({ runId, scope: "unresolved" })
					: null
			return { matched, ai }
		},
		onSuccess: ({ matched, ai }) => {
			toast.success(
				`Re-matched: ${matched.deterministicMatches} slots resolved, ${matched.unresolved} sent to the AI${ai ? ` (${ai.suggestionsAdded} suggestions)` : ""}`,
			)
		},
		onError: (e) => toast.error(`Re-match failed: ${e.message}`),
		onSettled: () => {
			setGridVersion((v) => v + 1)
			void queryClient.refetchQueries({ queryKey: runQueryKey })
		},
	})

	const runQuery = useQuery({
		queryKey: runQueryKey,
		enabled: isAuthReady && !!isSignedIn && !!runId,
		queryFn: () => client.aiMatcher.getRun({ runId }),
		refetchInterval: (query) =>
			rematchMutation.isPending || query.state.data?.status === "matching"
				? 1500
				: false,
	})

	// With `enabled: false` during auth boot, TanStack Query v5 reports
	// `status: "pending"` while `isLoading` stays false (no fetch yet). Branch
	// on `isPending` so the loading UI shows during auth init instead of
	// flashing the error block while waiting for Clerk to settle.
	if (runQuery.isPending) {
		return <div className="p-4 text-muted-foreground sm:p-6">Loading run…</div>
	}
	if (runQuery.isError) {
		return (
			<div className="p-4 text-bad sm:p-6">
				Failed to load run: {String(runQuery.error)}
			</div>
		)
	}
	if (!runQuery.data) {
		return (
			<div className="p-4 text-muted-foreground sm:p-6">Run not found.</div>
		)
	}

	const run = runQuery.data
	const aiQueried = run.aiSuggestions.length
	const aiWithCandidates = run.aiSuggestions.filter(
		(s) => s.candidates.length > 0,
	).length
	const aiTotalCandidates = run.aiSuggestions.reduce(
		(sum, s) => sum + s.candidates.length,
		0,
	)
	const aiEmpty = aiQueried - aiWithCandidates

	// Wrong-groups smell: the AI pass ran for a *meaningful* share of the run
	// and came back wholesale empty. A handful of unresolved-with-no-candidates
	// is expected (sealed niche items, vendor catalog gaps); only flag when the
	// AI was queried for ≥20 cells AND ≥50% of the run.
	const totalCells = run.ourProducts.length * Math.max(run.vendors.length, 1)
	const wrongGroupsSmell =
		aiQueried >= 20 && aiQueried >= totalCells * 0.5 && aiTotalCandidates === 0

	return (
		<div className="flex flex-col gap-4 p-4 sm:p-6">
			<div className="flex flex-col gap-1">
				<div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
					<h1 className="min-w-0 text-2xl font-semibold tracking-[-0.01em] break-words">
						AI Matcher: {run.gameSlug} / {run.expansionSlug}
					</h1>
					<div className="flex flex-wrap items-center gap-2">
						<Button
							size="sm"
							variant="outline"
							onClick={() => rematchMutation.mutate()}
							disabled={rematchMutation.isPending || run.status === "matching"}
							title="Re-run the matcher on this run's catalogs, then the AI pass on what it leaves open. Discards pending edits."
						>
							{rematchMutation.isPending ? (
								<Loader2 className="mr-1 h-3 w-3 animate-spin" />
							) : (
								<RefreshCw className="mr-1 h-3 w-3" />
							)}
							Re-match
						</Button>
						<Button
							size="sm"
							variant="outline"
							onClick={() => downloadRun(run)}
							title="Download this run to replay it locally with `cdnx ai-matcher import-run`"
						>
							<Download className="mr-1 h-3 w-3" />
							Export JSON
						</Button>
					</div>
				</div>
				<p className="text-muted-foreground text-sm">
					Status: <span className="font-mono">{run.status}</span> ·{" "}
					{run.ourProducts.length} cards · {run.tcgCandidates.length} TCG
					candidates · {run.cmCandidates.filter((c) => !c.outOfScope).length} CM
					candidates · {run.deterministicMatches.length} deterministic match
					{run.deterministicMatches.length === 1 ? "" : "es"} · AI: queried{" "}
					{aiQueried} cell{aiQueried === 1 ? "" : "s"}, found{" "}
					{aiTotalCandidates} candidate{aiTotalCandidates === 1 ? "" : "s"}
					{aiEmpty > 0 ? ` (${aiEmpty} returned empty)` : ""}
				</p>
				{run.status === "matching" && run.progress && (
					<p className="text-muted-foreground text-sm">
						{run.progress.label}: {run.progress.processed}/{run.progress.total}
					</p>
				)}
				{wrongGroupsSmell && (
					<div className="mt-2 rounded-md bg-warn-soft px-3 py-2 text-sm">
						<strong>Heads up:</strong> the AI was queried for {aiQueried} cells
						and every one came back empty. The most common cause is selecting
						vendor groups/expansions that don't contain your cards. Check that
						the TCGplayer group(s) and Cardmarket expansion code(s) you picked
						actually hold the cards from{" "}
						<span className="font-mono">{run.expansionSlug}</span>, then re-run.
					</div>
				)}
			</div>
			<AiMatcherGrid key={gridVersion} run={run} onRefresh={runQuery.refetch} />
		</div>
	)
}

/** Save the run snapshot as a JSON file for `cdnx ai-matcher import-run`. */
function downloadRun(run: { runId: string }) {
	const blob = new Blob([JSON.stringify(run)], { type: "application/json" })
	const url = URL.createObjectURL(blob)
	const link = document.createElement("a")
	link.href = url
	link.download = `ai-matcher-run-${run.runId}.json`
	link.click()
	URL.revokeObjectURL(url)
}
